import type { 
  IAdminRepository, 
  PlatformAdmin, 
  DashboardStats, 
  AuditLog,
  PlatformAnalytics 
} from '../interfaces/IAdminRepository';
import { mockPlatformAdmins, mockAuditLogs, simulateDelay, maybeSimulateError } from './mockData';

export class MockAdminRepository implements IAdminRepository {
  private admins: PlatformAdmin[] = [...mockPlatformAdmins];
  private logs: AuditLog[] = [...mockAuditLogs];

  async checkRole(userId: string): Promise<PlatformAdmin | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.admins.find(a => a.user_id === userId) || null;
  }

  async getDashboardStats(): Promise<DashboardStats> {
    await simulateDelay();
    maybeSimulateError();

    return {
      total_tenants: 25,
      active_tenants: 18,
      stalled_onboardings: 6,
      completed_today: 2,
      pending_invitations: 12,
      // Phase 5 additions
      total_business_units: 8,
      active_business_units: 7,
      business_units_with_managers: 5,
      total_client_agent_relationships: 12,
      active_relationships: 10,
      relationships_with_commission: 8,
      copyright_disclaimers_signed: 6,
      pending_board_resolutions: 3,
      total_credit_applications: 15,
      pending_credit_applications: 4,
      approved_credit_applications: 8,
      declined_credit_applications: 3,
    };
  }

  async getAnalytics(period: { start: string; end: string }): Promise<PlatformAnalytics> {
    await simulateDelay();
    maybeSimulateError();

    return {
      period: `${period.start} to ${period.end}`,
      total_signups: 25,
      completed_onboardings: 18,
      average_completion_time_days: 12,
      abandonment_rate: 28,
      most_common_stall_point: 'Account Owner onboarding',
    };
  }

  async getAuditLogs(filters?: {
    admin_user_id?: string;
    action?: string;
    target_type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> {
    await simulateDelay();
    maybeSimulateError();

    let filtered = [...this.logs];

    // Apply filters
    if (filters?.admin_user_id) {
      filtered = filtered.filter(l => l.admin_user_id === filters.admin_user_id);
    }
    if (filters?.action) {
      filtered = filtered.filter(l => l.action.includes(filters.action!));
    }
    if (filters?.target_type) {
      filtered = filtered.filter(l => l.target_type === filters.target_type);
    }
    if (filters?.start_date) {
      filtered = filtered.filter(l => l.created_at >= filters.start_date!);
    }
    if (filters?.end_date) {
      filtered = filtered.filter(l => l.created_at <= filters.end_date!);
    }

    const total = filtered.length;

    // Pagination
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);

    return { logs: paginated, total };
  }

  async logAction(data: {
    admin_user_id: string;
    action: string;
    target_type: string;
    target_id: string;
    details?: any;
    ip_address?: string;
    user_agent?: string;
  }): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const log: AuditLog = {
      id: crypto.randomUUID(),
      admin_user_id: data.admin_user_id,
      admin_email: 'admin@faces.com', // Mock value
      action: data.action,
      target_type: data.target_type as any,
      target_id: data.target_id,
      details: data.details,
      ip_address: data.ip_address,
      user_agent: data.user_agent,
      created_at: new Date().toISOString(),
    };

    this.logs.unshift(log); // Add to beginning
  }
}
